import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {RouteComponentProps, useLocation, useParams} from "react-router";
import {css, cx} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {FaqSection} from "@pg-mono/faq";
import {useScrollObserver} from "@pg-mono/hooks";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IOfferFriendlySubFilterGeneralUrlParams} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {LocationLinks} from "../../atoms/LocationLinks";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {HotlineListingPopup} from "../../hotline/components/HotlineListingPopup";
import {Country} from "../../region/types/Country";
import {UseGeolocationWithAlgolytics} from "../../search/utils/use_geolocation_with_algolytics";
import {useRegionAdServerRefresh} from "../../smart_ad_server/hooks/use_region_ad_server_refresh";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {OfferListLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {offerUrlParser} from "../helpers/friendly_offer_list/offer_url_parser";
import {useSmartAdServerOnOfferList} from "../hooks/use_smart_ad_server_on_offer_list";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {FloatingSemanticSearchSidebar} from "../list/components/FloatingSemanticSearchSidebar";
import {ListingStickyHeader} from "../list/components/ListingStickyHeader";
import {OfferAveragePrice} from "../list/components/OfferAveragePrice/OfferAveragePrice";
import {OfferList} from "../list/components/OfferList/OfferList";
import {OfferListGenericLocationDescription} from "../list/components/OfferListGenericLocationDescription/OfferListGenericLocationDescription";
import {OfferListNearbyOffers} from "../list/components/OfferListNearbyOffers";
import {OfferListSeoDescription} from "../list/components/OfferListSeoDescription";
import {OFFER_LIST_PAGE_SIZE} from "../list/constants/offer_list";
import {locationGenericSectionsToFaqContent} from "../list/helpers/location_generic_description/location_sections_to_faq_content";
import {
    getMultiRoomFlatFaqLocationSections,
    type MultiRoomFlatFaqRoomCount
} from "../list/helpers/location_generic_description/multi_room_flat_faq_location_sections";
import {studioOffersLocationGenericDescription} from "../list/helpers/location_generic_description/studio_offers_location_generic_description";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useFetchOfferList} from "../list/hooks/use_fetch_offer_list";
import {usePersistedFloatingSemanticSearchSidebarOpen} from "../list/hooks/use_persisted_floating_semantic_search_sidebar_open";
import {usePageLinkBuilder} from "../list/hooks/usePageLinkBuilder";
import {OfferListSubType} from "../list/types/OfferListSubType";
import {hasPriceFilter} from "../list/utils/has_price_filter";
import {SchemaListItem} from "../schemas/SchemaListItem";
import {OfferType} from "../types/OfferType";
import {getListingPageNearbyOffersPayload} from "../utils/get_listing_page_nearby_offers_payload";

export interface IProps extends RouteComponentProps<{offerListSubType?: OfferListSubType}> {}

export const OfferListView = (props: IProps) => {
    //  Init
    const {isMobile} = useUserDevice();
    const offerListRef = useRef<HTMLDivElement | null>(null);
    const fetchOfferList = useFetchOfferList();

    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);
    const list = useSelector((state: IRPStore) => state.offerList.list);
    const articles = useSelector((state: IRPStore) => state.offerList.articles);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    // Latches once the offer list has loaded at least once (incl. SSR-prefetched state). The mount-time
    // refetch flips `requestState` back to `Waiting`, so we can't rely on it alone to know the list is empty.
    const [hasOfferListLoadedOnce, setHasOfferListLoadedOnce] = useState(list.requestState === RequestState.Success);

    const search = useSelector((state: IRPStore) => state.offerList.search);
    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues);
    const nearbyRegions = useSelector((state: IRPStore) => state.offerList.nearbyRegions);
    const recommendedRegions = useSelector((state: IRPStore) => state.offerList.recommendedRegions);
    const metaDataTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const faq = useSelector((state: IRPStore) => state.faq.data);
    const selectedRegionEstimatedPrices = useSelector((state: IRPStore) => state.offerList.selectedRegionEstimatedPrices);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const location = useLocation();
    const latestQuery = useSelector((state: IRPStore) => state.offerList.list.latestQuery);
    const countryId = getCountryId(latestQuery.country);
    const isListEmpty = list.offers.length === 0;
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {locationAsked, locateUser} = UseGeolocationWithAlgolytics();

    useEffect(() => {
        /**
         * fetch offers on init including default distance on browser side - CU-862jpkfcy
         *
         * after dependencies changes in useEffect that triggers hitSearch search event was triggered 2 times on first list view entry, function in .then prevents this
         */
        fetchOfferList().then(() => {
            setInitialFetchDone(true);
        });
    }, []);

    useScrollObserver(
        ({scrollY}) => {
            const offerListOffsetTop = offerListRef.current?.offsetTop || 0;
            const offerListHeight = offerListRef.current?.clientHeight || 0;
            const locationAskBreakpoint = (offerListHeight + offerListOffsetTop) / 10;

            if (!locationAsked && scrollY >= locationAskBreakpoint) {
                locateUser();
            }
        },
        [offerListRef.current, locationAsked]
    );

    const singleRegion = selectedRegions.length === 1 ? selectedRegions[0] : undefined;
    const query = parseSearch(location.search);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const buildPageLink = usePageLinkBuilder();
    const {offerListSubFilter} = useParams<Partial<IOfferFriendlySubFilterGeneralUrlParams>>();

    const offerParams = useMemo(() => {
        const friendlySlug = (props.match.params as {friendlySlug?: string}).friendlySlug;
        return friendlySlug ? offerUrlParser(friendlySlug) : null;
    }, [props.match.params]);
    const {data: nearbyOffersResponse} = useGetOfferListQuery(getListingPageNearbyOffersPayload({offerParams, singleRegion}), {
        skip: list.requestState !== RequestState.Success
    });
    const offersNearby = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    const configureSmartAdServer = useSmartAdServerOnOfferList();
    const refreshSmartAds = useRegionAdServerRefresh();

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            setHasOfferListLoadedOnce(true);
            // on after list fetched
            initialFetchDone && hitSearch();
        }
    }, [list.requestState, initialFetchDone]);

    useHitUserSegmentLocationEntryOnce({
        enabled: list.requestState === RequestState.Success && selectedRegions.length > 0,
        regions: selectedRegions
    });

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            configureSmartAdServer(singleRegion);
            refreshSmartAds();
        }
    }, [list.requestState]);

    useEffect(() => {
        isGTMInitialized && list.requestState === RequestState.Success && gtmEventOffersCount(list.count);
    }, [list.requestState, isGTMInitialized]);

    const hitSearch = useCallback(() => {
        const {count, propertiesCount, latestQuery, offers} = list;

        const searchRegions: ISearchRegion[] = selectedRegions.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData({
            offers_count: count,
            properties_count: propertiesCount,
            latestQuery,
            searchRegions,
            query,
            travel_time: travelTime,
            locationSearchTab
        });
        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        offerListSearchHit(ViewType.OFFER_LIST, searchData, offerIds, offerNames, vendorIds, vendorNames);
    }, [list]);

    /**
     * Render
     */

    // With a price filter, API `count` can include offers without required prices; listing paginates by
    // offers that match the price filter (same as DefaultOfferListing / fetch_all_offers_for_count).
    const listPageSize = list.page_size || OFFER_LIST_PAGE_SIZE;
    const maxPageOffer = useMemo(() => {
        const priceScope = hasPriceFilter(searchFormValues) || hasPriceFilter(list.latestQuery);
        if (!priceScope) {
            return list.count ? Math.ceil(list.count / listPageSize) : 1;
        }
        if (list.allOffers.length > 0) {
            const withRequiredPrices = list.allOffers.filter((offer) => offer.has_required_prices === true).length;
            return withRequiredPrices ? Math.ceil(withRequiredPrices / listPageSize) : 1;
        }
        // `allOffers` cleared/refetching — fall back to API count (may overstate until fetch completes).
        return list.count ? Math.ceil(list.count / listPageSize) : 1;
    }, [list.count, list.allOffers, list.latestQuery, listPageSize, searchFormValues]);
    const maxPageSoldOffer = list.sold_offers_count ? Math.ceil(list.sold_offers_count / listPageSize) : 1;
    const maxPage = maxPageOffer + maxPageSoldOffer - 1;

    const currentPage = typeof query.page === "string" ? parseInt(query.page, 10) : 1;

    //  Check if search slug contains special listing and return its path if available
    const getSpecialListingPath = () => {
        return specialListing.some((el) => location.pathname.includes(el)) && selectedRegions.length <= 1 ? location.pathname.replace("/s/", "") : undefined;
    };

    type SpecialListingType = "tanie-" | "gotowe-" | "luksusowe-";
    // breadcrumbs
    const specialListing: SpecialListingType[] = ["tanie-", "gotowe-", "luksusowe-"];
    const offersTypeFromSearch = useSelector((store: IRPStore) => store.offerList?.search?.type);

    const offersType = offersTypeFromSearch || list.latestQuery.type ? parseInt(list.latestQuery.type as string, 10) : null;

    const minimalFlatListingAllowedFilterKeys = ["region", "type", "rooms_0", "rooms_1", "sort"];
    const minimalFlatListingExtraFilterKeys = Object.keys(offerParams || {}).filter((key) => !minimalFlatListingAllowedFilterKeys.includes(key));
    const isExactStudioOfferParams = offerParams?.rooms_1 === 1 && (offerParams.rooms_0 == null || offerParams.rooms_0 === 1);
    const isStudioFlatListing =
        currentPage === 1 && !!singleRegion && offersType === OfferType.FLAT && isExactStudioOfferParams && minimalFlatListingExtraFilterKeys.length === 0;

    const isExactMultiRoomOfferParams =
        typeof offerParams?.rooms_1 === "number" &&
        (offerParams.rooms_1 === 2 || offerParams.rooms_1 === 3 || offerParams.rooms_1 === 4) &&
        offerParams.rooms_0 === offerParams.rooms_1;
    const isMultiRoomFlatListing =
        currentPage === 1 && !!singleRegion && offersType === OfferType.FLAT && isExactMultiRoomOfferParams && minimalFlatListingExtraFilterKeys.length === 0;

    const studioFaqFromLocationDescription = useMemo(() => {
        if (!isStudioFlatListing || !singleRegion) {
            return null;
        }
        const sections = studioOffersLocationGenericDescription({
            region: singleRegion,
            selectedRegionEstimatedPrices
        });
        return locationGenericSectionsToFaqContent(sections);
    }, [isStudioFlatListing, selectedRegionEstimatedPrices, singleRegion]);

    const multiRoomFaqFromLocationDescription = useMemo(() => {
        if (!isMultiRoomFlatListing || !singleRegion || !offerParams?.rooms_1) {
            return null;
        }
        const sections = getMultiRoomFlatFaqLocationSections(offerParams.rooms_1 as MultiRoomFlatFaqRoomCount, singleRegion);
        return locationGenericSectionsToFaqContent(sections);
    }, [isMultiRoomFlatListing, offerParams?.rooms_1, singleRegion]);

    const faqContent = useMemo(() => {
        const fromApi = faq ?? [];
        const injectedFaq = studioFaqFromLocationDescription || multiRoomFaqFromLocationDescription;
        if (!injectedFaq) {
            return fromApi;
        }
        return [injectedFaq, ...fromApi];
    }, [faq, studioFaqFromLocationDescription, multiRoomFaqFromLocationDescription]);

    // Data prepared here makes the BreadCrumbs component lighter since it is used in OfferList, OfferDetail, PropertyDetail
    const getBreadcrumbs = useMemo(() => {
        if (singleRegion && selectedRegions[0]) {
            const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
                selectedRegions[0]?.stats || {};

            return {
                region_type_voivodeship: region_type_voivodeship || null,
                region_type_city: region_type_city || null,
                region_type_county: region_type_county || null,
                region_type_district: region_type_district || null,
                region_type_housing_estate: region_type_housing_estate || null,
                region_type_town: region_type_town || null
            };
        }
        return {
            region_type_voivodeship: null,
            region_type_city: null,
            region_type_county: null,
            region_type_district: null,
            region_type_housing_estate: null,
            region_type_town: null
        };
    }, [singleRegion]);

    const fixedSidebarSectionRef = useRef<HTMLDivElement | null>(null);

    const isAiAdvisorContentLoading = !initialFetchDone;
    // Open/closed state is owned here (single source of truth) so the listing can reclaim the reserved
    // column when the advisor is collapsed, and the floating sidebar stays in sync.
    const [isSidebarOpen, setIsSidebarOpen] = usePersistedFloatingSemanticSearchSidebarOpen();
    // Listing is "known empty" once it has loaded at least once and has no offers. Using the latched
    // `hasOfferListLoadedOnce` (instead of the live `requestState`) keeps this stable through the mount-time
    // refetch, so the AI advisor skeleton never flashes when we already know there are no results.
    const isListKnownEmpty = isListEmpty && hasOfferListLoadedOnce;
    // Reserve the AI advisor column only when the panel is actually shown — not for known-empty listings,
    // and not when the user collapsed it — so the listing stretches to full width otherwise. For
    // SSR-prefetched empty results this is already correct on the first paint, so it introduces no CLS.
    const isSidebarReserved = !isMobile && !isListKnownEmpty && isSidebarOpen;

    return (
        <>
            <article>
                <ListingStickyHeader latestQuery={latestQuery} hideBottomPadding />

                <div ref={fixedSidebarSectionRef} className={fixedSidebarContainer}>
                    <div className={cx(!isMobile && floatingSemanticSearchSidebarWrapper)}>
                        <div className={cx(!isMobile && floatingSemanticSearchSidebarContentWrapper, isSidebarReserved && reservedSidebarContentWrapper)}>
                            <WideContent className={breadcrumbsOuterWrapper}>
                                <div className={cx(breadcrumbsWrapper, breadcrumbsWrapperWithTopReelsStyle)}>
                                    <OfferAndPropertyBreadcrumbs
                                        type={(offersType as OfferType) || null}
                                        breadcrumbs={getBreadcrumbs}
                                        specialListing={getSpecialListingPath()}
                                        country={latestQuery.country}
                                    />
                                </div>

                                {!!isMobile && selectedRegions.length === 1 && (
                                    <OfferAveragePrice singleRegion={selectedRegions[0]} noText className={headerInfoIconWrapper} />
                                )}
                            </WideContent>

                            <OfferList
                                ref={offerListRef}
                                list={list}
                                metaDataTitle={metaDataTitle}
                                selectedRegion={selectedRegions}
                                className={css({mb: 3})}
                                offerListSubType={props.match.params.offerListSubType}
                                isSidebarReserved={isSidebarReserved}
                                isInitialOfferListFetchDone={initialFetchDone}
                            />

                            {maxPage > 1 && (
                                <WideContent className={paginationHolder}>
                                    <Pagination
                                        size={isMobile ? PaginationSize.MD : PaginationSize.LG}
                                        pageCount={maxPage}
                                        currentPage={currentPage}
                                        getPageLink={buildPageLink}
                                        siblingsCount={isMobile ? 0 : 1}
                                        className={paginationStyle}
                                    />
                                </WideContent>
                            )}

                            {!isListKnownEmpty && (!isListEmpty || isAiAdvisorContentLoading) && (
                                <FloatingSemanticSearchSidebar
                                    sectionRef={fixedSidebarSectionRef}
                                    isContentLoading={isAiAdvisorContentLoading}
                                    isOpen={isSidebarOpen}
                                    setIsOpen={setIsSidebarOpen}
                                />
                            )}

                            <SchemaListItem offers={list.offers} />

                            {/* Commented out due to RP-217 requirements */}
                            {/* <WideContent className={css({mb: "calc(var(--globals-baseUnit) * 12)"})}>
                    <ListingBanner />
                </WideContent> */}
                            {singleRegion && offersNearby.length ? (
                                <WideContent className={nearbyOffersHolder}>
                                    <OfferListNearbyOffers
                                        offerParams={offerParams}
                                        viewType={ViewType.OFFER_LIST}
                                        regionWhere={selectedRegions[0].name_declension_where}
                                        isSidebarReserved={isSidebarReserved}
                                    />
                                </WideContent>
                            ) : null}
                        </div>
                    </div>
                </div>

                {singleRegion && articles.length > 0 && (
                    <RelatedArticlesSection
                        as="h2"
                        heading={`Wiadomości na temat ${singleRegion?.name_declension_what}`}
                        articles={articles}
                        className={articleSectionStyles}
                    />
                )}

                {!offerListSubFilter && !latestQuery.floor_choices && list.page === 1 && search?.seo_description && (
                    <WideContent>
                        <OfferListSeoDescription seo_description={search.seo_description} />
                    </WideContent>
                )}

                {currentPage === 1 && (
                    <div className={isMobile ? faqSectionMobileSpacing : undefined}>
                        <FaqSection faqContent={faqContent} />
                    </div>
                )}

                {!offerListSubFilter && !latestQuery.floor_choices && singleRegion && !isListEmpty && (
                    <WideContent>
                        <OfferListGenericLocationDescription
                            offerType={offersType as OfferType}
                            filters={offerParams}
                            region={singleRegion}
                            className={locationDescriptionStyles}
                        />
                    </WideContent>
                )}

                {search && (
                    <OfferListLocationLinksWrapper className={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            nearbyRegions={nearbyRegions}
                            offerType={search.type}
                            isOfferListEmpty={!list.offers.length}
                            region={singleRegion}
                        />
                    </OfferListLocationLinksWrapper>
                )}
                {countryId !== Country.POLAND && !search && (
                    <OfferListLocationLinksWrapper className={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            offerType={Number(latestQuery.type)}
                            isOfferListEmpty={!list.offers.length}
                            country={countryId}
                            abroadVoivodeshipRegions
                        />
                    </OfferListLocationLinksWrapper>
                )}
            </article>
            <FavouritesInfoModal />
            <LeadModal />
            <ForYouModal />
            <HotlineListingPopup offerTypes={offersType ? [offersType] : [OfferType.HOUSE, OfferType.FLAT]} />
        </>
    );
};

//  Styles
const breadcrumbsOuterWrapper = css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    py: 1,
    height: "33px",
    mt: 3
});

const faqSectionMobileSpacing = css({
    mt: 2
});

const breadcrumbsWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mt: 0,
    mx: 0,
    mb: 2,
    overflowY: "scroll",
    md: {
        scrollbarWidth: "none",

        "&::-webkit-scrollbar": {
            display: "none"
        }
    }
});

const breadcrumbsWrapperWithTopReelsStyle = css({
    mt: -3,
    mb: -2
});

const headerInfoIconWrapper = css({
    pl: 1,
    flex: "0 0 calc(var(--globals-baseUnit) * 4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "calc(var(--globals-baseUnit) * 4)",
    height: "calc(var(--globals-baseUnit) * 3)",

    "& > div": {
        pt: 0
    }
});

const paginationHolder = css({
    px: 0,
    py: 6
});

const nearbyOffersHolder = css({
    mb: 5,
    md: {
        mb: 10
    }
});

const locationLinksWrapper = css({
    mt: 0,
    mx: 0,
    mb: "3.5rem !important",

    md: {
        mb: "4.2rem !important"
    }
});

const locationDescriptionStyles = css({
    mb: 0.5,
    md: {
        mb: "1.8rem"
    }
});

const articleSectionStyles = css({
    md: {
        mb: "3.6rem"
    }
});

const paginationStyle = css({
    columnGap: 0.5,
    "& ul": {
        columnGap: 0.5
    },

    xs: {
        columnGap: 1,
        "& ul": {
            columnGap: 1
        }
    }
});

const fixedSidebarContainer = css({
    position: "relative",
    overflow: "hidden"
});

const floatingSemanticSearchSidebarWrapper = css({
    maxWidth: "1576px",
    margin: "0 auto",
    display: "flex"
});

const floatingSemanticSearchSidebarContentWrapper = css({
    width: "100%"
});

// Permanently reserve room for the floating AI sidebar (488px panel + 32px gap = 520px) so the offer
// grid never sits under the panel rail. Pure CSS (no JS measurement) keeps the reservation correct on
// the SSR / first paint, eliminating the post-hydration reflow (CLS). The reservation shrinks by
// whatever right margin the centered 1576px container already provides and disappears once the viewport
// is wide enough (~2616px) to fit the panel entirely in that margin — matching the legacy JS behavior.
const reservedSidebarContentWrapper = css({
    boxSizing: "border-box",
    paddingRight: "max(0px, calc(520px - max(0px, calc((100vw - 1576px) / 2))))"
});
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {RouteComponentProps, useLocation, useParams} from "react-router";
import {css, cx} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {FaqSection} from "@pg-mono/faq";
import {useScrollObserver} from "@pg-mono/hooks";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IOfferFriendlySubFilterGeneralUrlParams} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {LocationLinks} from "../../atoms/LocationLinks";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {HotlineListingPopup} from "../../hotline/components/HotlineListingPopup";
import {Country} from "../../region/types/Country";
import {UseGeolocationWithAlgolytics} from "../../search/utils/use_geolocation_with_algolytics";
import {useRegionAdServerRefresh} from "../../smart_ad_server/hooks/use_region_ad_server_refresh";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {OfferListLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {offerUrlParser} from "../helpers/friendly_offer_list/offer_url_parser";
import {useSmartAdServerOnOfferList} from "../hooks/use_smart_ad_server_on_offer_list";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {FloatingSemanticSearchSidebar} from "../list/components/FloatingSemanticSearchSidebar";
import {ListingStickyHeader} from "../list/components/ListingStickyHeader";
import {OfferAveragePrice} from "../list/components/OfferAveragePrice/OfferAveragePrice";
import {OfferList} from "../list/components/OfferList/OfferList";
import {OfferListGenericLocationDescription} from "../list/components/OfferListGenericLocationDescription/OfferListGenericLocationDescription";
import {OfferListNearbyOffers} from "../list/components/OfferListNearbyOffers";
import {OfferListSeoDescription} from "../list/components/OfferListSeoDescription";
import {OFFER_LIST_PAGE_SIZE} from "../list/constants/offer_list";
import {locationGenericSectionsToFaqContent} from "../list/helpers/location_generic_description/location_sections_to_faq_content";
import {
    getMultiRoomFlatFaqLocationSections,
    type MultiRoomFlatFaqRoomCount
} from "../list/helpers/location_generic_description/multi_room_flat_faq_location_sections";
import {studioOffersLocationGenericDescription} from "../list/helpers/location_generic_description/studio_offers_location_generic_description";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useFetchOfferList} from "../list/hooks/use_fetch_offer_list";
import {usePersistedFloatingSemanticSearchSidebarOpen} from "../list/hooks/use_persisted_floating_semantic_search_sidebar_open";
import {usePageLinkBuilder} from "../list/hooks/usePageLinkBuilder";
import {OfferListSubType} from "../list/types/OfferListSubType";
import {hasPriceFilter} from "../list/utils/has_price_filter";
import {SchemaListItem} from "../schemas/SchemaListItem";
import {OfferType} from "../types/OfferType";
import {getListingPageNearbyOffersPayload} from "../utils/get_listing_page_nearby_offers_payload";

export interface IProps extends RouteComponentProps<{offerListSubType?: OfferListSubType}> {}

export const OfferListView = (props: IProps) => {
    //  Init
    const {isMobile} = useUserDevice();
    const offerListRef = useRef<HTMLDivElement | null>(null);
    const fetchOfferList = useFetchOfferList();

    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);
    const list = useSelector((state: IRPStore) => state.offerList.list);
    const articles = useSelector((state: IRPStore) => state.offerList.articles);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    // Latches once the offer list has loaded at least once (incl. SSR-prefetched state). The mount-time
    // refetch flips `requestState` back to `Waiting`, so we can't rely on it alone to know the list is empty.
    const [hasOfferListLoadedOnce, setHasOfferListLoadedOnce] = useState(list.requestState === RequestState.Success);

    const search = useSelector((state: IRPStore) => state.offerList.search);
    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues);
    const nearbyRegions = useSelector((state: IRPStore) => state.offerList.nearbyRegions);
    const recommendedRegions = useSelector((state: IRPStore) => state.offerList.recommendedRegions);
    const metaDataTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const faq = useSelector((state: IRPStore) => state.faq.data);
    const selectedRegionEstimatedPrices = useSelector((state: IRPStore) => state.offerList.selectedRegionEstimatedPrices);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const location = useLocation();
    const latestQuery = useSelector((state: IRPStore) => state.offerList.list.latestQuery);
    const countryId = getCountryId(latestQuery.country);
    const isListEmpty = list.offers.length === 0;
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {locationAsked, locateUser} = UseGeolocationWithAlgolytics();

    useEffect(() => {
        /**
         * fetch offers on init including default distance on browser side - CU-862jpkfcy
         *
         * after dependencies changes in useEffect that triggers hitSearch search event was triggered 2 times on first list view entry, function in .then prevents this
         */
        fetchOfferList().then(() => {
            setInitialFetchDone(true);
        });
    }, []);

    useScrollObserver(
        ({scrollY}) => {
            const offerListOffsetTop = offerListRef.current?.offsetTop || 0;
            const offerListHeight = offerListRef.current?.clientHeight || 0;
            const locationAskBreakpoint = (offerListHeight + offerListOffsetTop) / 10;

            if (!locationAsked && scrollY >= locationAskBreakpoint) {
                locateUser();
            }
        },
        [offerListRef.current, locationAsked]
    );

    const singleRegion = selectedRegions.length === 1 ? selectedRegions[0] : undefined;
    const query = parseSearch(location.search);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const buildPageLink = usePageLinkBuilder();
    const {offerListSubFilter} = useParams<Partial<IOfferFriendlySubFilterGeneralUrlParams>>();

    const offerParams = useMemo(() => {
        const friendlySlug = (props.match.params as {friendlySlug?: string}).friendlySlug;
        return friendlySlug ? offerUrlParser(friendlySlug) : null;
    }, [props.match.params]);
    const {data: nearbyOffersResponse} = useGetOfferListQuery(getListingPageNearbyOffersPayload({offerParams, singleRegion}), {
        skip: list.requestState !== RequestState.Success
    });
    const offersNearby = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    const configureSmartAdServer = useSmartAdServerOnOfferList();
    const refreshSmartAds = useRegionAdServerRefresh();

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            setHasOfferListLoadedOnce(true);
            // on after list fetched
            initialFetchDone && hitSearch();
        }
    }, [list.requestState, initialFetchDone]);

    useHitUserSegmentLocationEntryOnce({
        enabled: list.requestState === RequestState.Success && selectedRegions.length > 0,
        regions: selectedRegions
    });

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            configureSmartAdServer(singleRegion);
            refreshSmartAds();
        }
    }, [list.requestState]);

    useEffect(() => {
        isGTMInitialized && list.requestState === RequestState.Success && gtmEventOffersCount(list.count);
    }, [list.requestState, isGTMInitialized]);

    const hitSearch = useCallback(() => {
        const {count, propertiesCount, latestQuery, offers} = list;

        const searchRegions: ISearchRegion[] = selectedRegions.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData({
            offers_count: count,
            properties_count: propertiesCount,
            latestQuery,
            searchRegions,
            query,
            travel_time: travelTime,
            locationSearchTab
        });
        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        offerListSearchHit(ViewType.OFFER_LIST, searchData, offerIds, offerNames, vendorIds, vendorNames);
    }, [list]);

    /**
     * Render
     */

    // With a price filter, API `count` can include offers without required prices; listing paginates by
    // offers that match the price filter (same as DefaultOfferListing / fetch_all_offers_for_count).
    const listPageSize = list.page_size || OFFER_LIST_PAGE_SIZE;
    const maxPageOffer = useMemo(() => {
        const priceScope = hasPriceFilter(searchFormValues) || hasPriceFilter(list.latestQuery);
        if (!priceScope) {
            return list.count ? Math.ceil(list.count / listPageSize) : 1;
        }
        if (list.allOffers.length > 0) {
            const withRequiredPrices = list.allOffers.filter((offer) => offer.has_required_prices === true).length;
            return withRequiredPrices ? Math.ceil(withRequiredPrices / listPageSize) : 1;
        }
        // `allOffers` cleared/refetching — fall back to API count (may overstate until fetch completes).
        return list.count ? Math.ceil(list.count / listPageSize) : 1;
    }, [list.count, list.allOffers, list.latestQuery, listPageSize, searchFormValues]);
    const maxPageSoldOffer = list.sold_offers_count ? Math.ceil(list.sold_offers_count / listPageSize) : 1;
    const maxPage = maxPageOffer + maxPageSoldOffer - 1;

    const currentPage = typeof query.page === "string" ? parseInt(query.page, 10) : 1;

    //  Check if search slug contains special listing and return its path if available
    const getSpecialListingPath = () => {
        return specialListing.some((el) => location.pathname.includes(el)) && selectedRegions.length <= 1 ? location.pathname.replace("/s/", "") : undefined;
    };

    type SpecialListingType = "tanie-" | "gotowe-" | "luksusowe-";
    // breadcrumbs
    const specialListing: SpecialListingType[] = ["tanie-", "gotowe-", "luksusowe-"];
    const offersTypeFromSearch = useSelector((store: IRPStore) => store.offerList?.search?.type);

    const offersType = offersTypeFromSearch || list.latestQuery.type ? parseInt(list.latestQuery.type as string, 10) : null;

    const minimalFlatListingAllowedFilterKeys = ["region", "type", "rooms_0", "rooms_1", "sort"];
    const minimalFlatListingExtraFilterKeys = Object.keys(offerParams || {}).filter((key) => !minimalFlatListingAllowedFilterKeys.includes(key));
    const isExactStudioOfferParams = offerParams?.rooms_1 === 1 && (offerParams.rooms_0 == null || offerParams.rooms_0 === 1);
    const isStudioFlatListing =
        currentPage === 1 && !!singleRegion && offersType === OfferType.FLAT && isExactStudioOfferParams && minimalFlatListingExtraFilterKeys.length === 0;

    const isExactMultiRoomOfferParams =
        typeof offerParams?.rooms_1 === "number" &&
        (offerParams.rooms_1 === 2 || offerParams.rooms_1 === 3 || offerParams.rooms_1 === 4) &&
        offerParams.rooms_0 === offerParams.rooms_1;
    const isMultiRoomFlatListing =
        currentPage === 1 && !!singleRegion && offersType === OfferType.FLAT && isExactMultiRoomOfferParams && minimalFlatListingExtraFilterKeys.length === 0;

    const studioFaqFromLocationDescription = useMemo(() => {
        if (!isStudioFlatListing || !singleRegion) {
            return null;
        }
        const sections = studioOffersLocationGenericDescription({
            region: singleRegion,
            selectedRegionEstimatedPrices
        });
        return locationGenericSectionsToFaqContent(sections);
    }, [isStudioFlatListing, selectedRegionEstimatedPrices, singleRegion]);

    const multiRoomFaqFromLocationDescription = useMemo(() => {
        if (!isMultiRoomFlatListing || !singleRegion || !offerParams?.rooms_1) {
            return null;
        }
        const sections = getMultiRoomFlatFaqLocationSections(offerParams.rooms_1 as MultiRoomFlatFaqRoomCount, singleRegion);
        return locationGenericSectionsToFaqContent(sections);
    }, [isMultiRoomFlatListing, offerParams?.rooms_1, singleRegion]);

    const faqContent = useMemo(() => {
        const fromApi = faq ?? [];
        const injectedFaq = studioFaqFromLocationDescription || multiRoomFaqFromLocationDescription;
        if (!injectedFaq) {
            return fromApi;
        }
        return [injectedFaq, ...fromApi];
    }, [faq, studioFaqFromLocationDescription, multiRoomFaqFromLocationDescription]);

    // Data prepared here makes the BreadCrumbs component lighter since it is used in OfferList, OfferDetail, PropertyDetail
    const getBreadcrumbs = useMemo(() => {
        if (singleRegion && selectedRegions[0]) {
            const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
                selectedRegions[0]?.stats || {};

            return {
                region_type_voivodeship: region_type_voivodeship || null,
                region_type_city: region_type_city || null,
                region_type_county: region_type_county || null,
                region_type_district: region_type_district || null,
                region_type_housing_estate: region_type_housing_estate || null,
                region_type_town: region_type_town || null
            };
        }
        return {
            region_type_voivodeship: null,
            region_type_city: null,
            region_type_county: null,
            region_type_district: null,
            region_type_housing_estate: null,
            region_type_town: null
        };
    }, [singleRegion]);

    const fixedSidebarSectionRef = useRef<HTMLDivElement | null>(null);

    const isAiAdvisorContentLoading = !initialFetchDone;
    // Open/closed state is owned here (single source of truth) so the listing can reclaim the reserved
    // column when the advisor is collapsed, and the floating sidebar stays in sync.
    const [isSidebarOpen, setIsSidebarOpen] = usePersistedFloatingSemanticSearchSidebarOpen();
    // Listing is "known empty" once it has loaded at least once and has no offers. Using the latched
    // `hasOfferListLoadedOnce` (instead of the live `requestState`) keeps this stable through the mount-time
    // refetch, so the AI advisor skeleton never flashes when we already know there are no results.
    const isListKnownEmpty = isListEmpty && hasOfferListLoadedOnce;
    // Reserve the AI advisor column only when the panel is actually shown — not for known-empty listings,
    // and not when the user collapsed it — so the listing stretches to full width otherwise. For
    // SSR-prefetched empty results this is already correct on the first paint, so it introduces no CLS.
    const isSidebarReserved = !isMobile && !isListKnownEmpty && isSidebarOpen;

    return (
        <>
            <article>
                <ListingStickyHeader latestQuery={latestQuery} hideBottomPadding />

                <div ref={fixedSidebarSectionRef} className={fixedSidebarContainer}>
                    <div className={cx(!isMobile && floatingSemanticSearchSidebarWrapper)}>
                        <div className={cx(!isMobile && floatingSemanticSearchSidebarContentWrapper, isSidebarReserved && reservedSidebarContentWrapper)}>
                            <WideContent className={breadcrumbsOuterWrapper}>
                                <div className={cx(breadcrumbsWrapper, breadcrumbsWrapperWithTopReelsStyle)}>
                                    <OfferAndPropertyBreadcrumbs
                                        type={(offersType as OfferType) || null}
                                        breadcrumbs={getBreadcrumbs}
                                        specialListing={getSpecialListingPath()}
                                        country={latestQuery.country}
                                    />
                                </div>

                                {!!isMobile && selectedRegions.length === 1 && (
                                    <OfferAveragePrice singleRegion={selectedRegions[0]} noText className={headerInfoIconWrapper} />
                                )}
                            </WideContent>

                            <OfferList
                                ref={offerListRef}
                                list={list}
                                metaDataTitle={metaDataTitle}
                                selectedRegion={selectedRegions}
                                className={css({mb: 3})}
                                offerListSubType={props.match.params.offerListSubType}
                                isSidebarReserved={isSidebarReserved}
                                isInitialOfferListFetchDone={initialFetchDone}
                            />

                            {maxPage > 1 && (
                                <WideContent className={paginationHolder}>
                                    <Pagination
                                        size={isMobile ? PaginationSize.MD : PaginationSize.LG}
                                        pageCount={maxPage}
                                        currentPage={currentPage}
                                        getPageLink={buildPageLink}
                                        siblingsCount={isMobile ? 0 : 1}
                                        className={paginationStyle}
                                    />
                                </WideContent>
                            )}

                            {!isListKnownEmpty && (!isListEmpty || isAiAdvisorContentLoading) && (
                                <FloatingSemanticSearchSidebar
                                    sectionRef={fixedSidebarSectionRef}
                                    isContentLoading={isAiAdvisorContentLoading}
                                    isOpen={isSidebarOpen}
                                    setIsOpen={setIsSidebarOpen}
                                />
                            )}

                            <SchemaListItem offers={list.offers} />

                            {/* Commented out due to RP-217 requirements */}
                            {/* <WideContent className={css({mb: "calc(var(--globals-baseUnit) * 12)"})}>
                    <ListingBanner />
                </WideContent> */}
                            {singleRegion && offersNearby.length ? (
                                <WideContent className={nearbyOffersHolder}>
                                    <OfferListNearbyOffers
                                        offerParams={offerParams}
                                        viewType={ViewType.OFFER_LIST}
                                        regionWhere={selectedRegions[0].name_declension_where}
                                        isSidebarReserved={isSidebarReserved}
                                    />
                                </WideContent>
                            ) : null}
                        </div>
                    </div>
                </div>

                {singleRegion && articles.length > 0 && (
                    <RelatedArticlesSection
                        as="h2"
                        heading={`Wiadomości na temat ${singleRegion?.name_declension_what}`}
                        articles={articles}
                        className={articleSectionStyles}
                    />
                )}

                {!offerListSubFilter && !latestQuery.floor_choices && list.page === 1 && search?.seo_description && (
                    <WideContent>
                        <OfferListSeoDescription seo_description={search.seo_description} />
                    </WideContent>
                )}

                {currentPage === 1 && (
                    <div className={isMobile ? faqSectionMobileSpacing : undefined}>
                        <FaqSection faqContent={faqContent} />
                    </div>
                )}

                {!offerListSubFilter && !latestQuery.floor_choices && singleRegion && !isListEmpty && (
                    <WideContent>
                        <OfferListGenericLocationDescription
                            offerType={offersType as OfferType}
                            filters={offerParams}
                            region={singleRegion}
                            className={locationDescriptionStyles}
                        />
                    </WideContent>
                )}

                {search && (
                    <OfferListLocationLinksWrapper className={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            nearbyRegions={nearbyRegions}
                            offerType={search.type}
                            isOfferListEmpty={!list.offers.length}
                            region={singleRegion}
                        />
                    </OfferListLocationLinksWrapper>
                )}
                {countryId !== Country.POLAND && !search && (
                    <OfferListLocationLinksWrapper className={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            offerType={Number(latestQuery.type)}
                            isOfferListEmpty={!list.offers.length}
                            country={countryId}
                            abroadVoivodeshipRegions
                        />
                    </OfferListLocationLinksWrapper>
                )}
            </article>
            <FavouritesInfoModal />
            <LeadModal />
            <ForYouModal />
            <HotlineListingPopup offerTypes={offersType ? [offersType] : [OfferType.HOUSE, OfferType.FLAT]} />
        </>
    );
};

//  Styles
const breadcrumbsOuterWrapper = css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    py: 1,
    height: "33px",
    mt: 3
});

const faqSectionMobileSpacing = css({
    mt: 2
});

const breadcrumbsWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mt: 0,
    mx: 0,
    mb: 2,
    overflowY: "scroll",
    md: {
        scrollbarWidth: "none",

        "&::-webkit-scrollbar": {
            display: "none"
        }
    }
});

const breadcrumbsWrapperWithTopReelsStyle = css({
    mt: -3,
    mb: -2
});

const headerInfoIconWrapper = css({
    pl: 1,
    flex: "0 0 calc(var(--globals-baseUnit) * 4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "calc(var(--globals-baseUnit) * 4)",
    height: "calc(var(--globals-baseUnit) * 3)",

    "& > div": {
        pt: 0
    }
});

const paginationHolder = css({
    px: 0,
    py: 6
});

const nearbyOffersHolder = css({
    mb: 5,
    md: {
        mb: 10
    }
});

const locationLinksWrapper = css({
    mt: 0,
    mx: 0,
    mb: "3.5rem !important",

    md: {
        mb: "4.2rem !important"
    }
});

const locationDescriptionStyles = css({
    mb: 0.5,
    md: {
        mb: "1.8rem"
    }
});

const articleSectionStyles = css({
    md: {
        mb: "3.6rem"
    }
});

const paginationStyle = css({
    columnGap: 0.5,
    "& ul": {
        columnGap: 0.5
    },

    xs: {
        columnGap: 1,
        "& ul": {
            columnGap: 1
        }
    }
});

const fixedSidebarContainer = css({
    position: "relative",
    overflow: "hidden"
});

const floatingSemanticSearchSidebarWrapper = css({
    maxWidth: "1576px",
    margin: "0 auto",
    display: "flex"
});

const floatingSemanticSearchSidebarContentWrapper = css({
    width: "100%"
});

// Permanently reserve room for the floating AI sidebar (488px panel + 32px gap = 520px) so the offer
// grid never sits under the panel rail. Pure CSS (no JS measurement) keeps the reservation correct on
// the SSR / first paint, eliminating the post-hydration reflow (CLS). The reservation shrinks by
// whatever right margin the centered 1576px container already provides and disappears once the viewport
// is wide enough (~2616px) to fit the panel entirely in that margin — matching the legacy JS behavior.
const reservedSidebarContentWrapper = css({
    boxSizing: "border-box",
    paddingRight: "max(0px, calc(520px - max(0px, calc((100vw - 1576px) / 2))))"
});
